import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { MediaBtnContainer } from '../../../pre-meeting/components/Buttons'
import { Tip } from '../../../../components/Tip'
import { useZoomContext } from '../../../../context/ZoomContext'
import { useTranslation } from 'react-i18next'
import { useSessionParams } from '../../../../hooks/useSessionParams'
import { useUserCourses } from '../../../../pages/meeting-view/hooks/useCourses'
import { useContentSharing } from '../../../../context/ContentSharingContext'
import StopSharingModal from '../../../../components/Modals/StopSharingModal'
import StartSharingModal from '../../../../components/Modals/StartSharingModal'
import styled from 'styled-components'

// Create styled MediaBtnContainer with animation
const AnimatedMediaBtnContainer = styled(MediaBtnContainer)`
    background: linear-gradient(
        135deg,
        rgba(82, 20, 204, 0.95) 0%,
        rgba(154, 20, 238, 0.95) 30%,
        rgba(20, 172, 255, 0.95) 70%,
        rgba(82, 20, 204, 0.95) 100%
    );
    background-size: 200% 200%;
    animation: gradientAnimation 8s ease infinite;
    opacity: 1;

    @keyframes gradientAnimation {
        0% {
            background-position: 0% 50%;
        }
        50% {
            background-position: 100% 50%;
        }
        100% {
            background-position: 0% 50%;
        }
    }
`

export const ShareContent = () => {
    const { t } = useTranslation('meeting')
    const { setSidebarMode, sidebarMode, setConfirmationDialog } = useZoomContext()
    const { isContentShared, isContentSharer, shareContent, stopSharingContent } = useContentSharing()
    const { room } = useSessionParams()
    const { currentCourse } = useUserCourses()
    const enableSharedContent = !!currentCourse?.courseFlags.enableSharedContent

    // Don't render the component if content sharing is not enabled for this course
    // or if we're in the general room
    if (!enableSharedContent || room === 'general') return null

    const handleStopSharing = () => {
        // If user is sharing content, show confirmation modal
        if (isContentSharer) {
            setConfirmationDialog(
                <StopSharingModal
                    onConfirm={() => {
                        // Stop sharing and close the modal and sidebar
                        stopSharingContent()
                        setSidebarMode(null)
                        setConfirmationDialog(undefined)
                    }}
                    onCancel={() => {
                        // Just close the modal without stopping sharing
                        setConfirmationDialog(undefined)
                    }}
                />
            )
        }
    }

    const handleStartSharing = () => {
        setConfirmationDialog(
            <StartSharingModal
                onConfirm={() => {
                    // Start sharing and open the sidebar
                    shareContent()
                    setSidebarMode('content')
                    setConfirmationDialog(undefined)
                }}
                onCancel={() => {
                    // Just close the modal without starting sharing
                    setConfirmationDialog(undefined)
                }}
            />
        )
    }

    const handleClick = (e: React.MouseEvent) => {
        e.stopPropagation()

        if (!isContentShared) {
            // If no one is sharing content, show start sharing modal
            handleStartSharing()
        } else if (isContentSharer) {
            // If I'm sharing content
            if (sidebarMode === 'content') {
                // If content panel is already open, show confirmation to stop sharing
                handleStopSharing()
            } else {
                // If content panel is not open, just open it without stopping sharing
                setSidebarMode('content')
            }
        } else if (isContentShared && !isContentSharer) {
            // If someone else is sharing, just toggle the sidebar visibility
            setSidebarMode(oldSidebarMode => {
                return oldSidebarMode === 'content' ? null : 'content'
            })
        }
    }

    const tooltipText =
        isContentShared && isContentSharer
            ? sidebarMode === 'content'
                ? t('controls.stopSharingContent')
                : t('controls.viewSharedContent')
            : isContentShared && !isContentSharer
            ? sidebarMode === 'content'
                ? t('controls.hideContent')
                : t('controls.viewSharedContent')
            : sidebarMode === 'content'
            ? t('controls.hideContent')
            : t('controls.shareContent')

    // Determine if the button should be visually active
    const isActive = sidebarMode === 'content' || (isContentShared && isContentSharer)

    return (
        <div>
            <AnimatedMediaBtnContainer
                active={isActive}
                wide
                accent
                onClick={handleClick}
                data-tooltip-id="share-ai-action"
            >
                <span style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <FontAwesomeIcon className="icon" icon={['far', 'folder']} />
                </span>
            </AnimatedMediaBtnContainer>

            <Tip id="share-ai-action" content={tooltipText} />
        </div>
    )
}
